import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, NgZone, Inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AmazonConnectApp, AppContactScope, AppCreateEvent } from '@amazon-connect/app';
import { CallSummaryService } from './call-summary.service';
import { CallIntentService } from './call-intent.service';
import { ContactClient } from '@amazon-connect/contact';
import { CallerService } from '../caller.service';
import { Router } from '@angular/router';
import { AppConfig, APP_CONFIG } from 'src/app.config';
import { Store } from '@ngrx/store';
import { loadPolicyDetails, loadPolicyData } from '../store/policy/policy.actions';
import { addRecentPolicy } from '../store/recent-policies/recent-policies.actions';
import * as PolicyActions from 'src/app/store/policy/policy.actions';

@Injectable({
    providedIn: 'root',
})
export class ConnectService {
    private initialized = false;

    contactId: string | null = null;
    channel: string | null = null;
    attributes: any = {};
    callSummary: string | null = null;
    preEscalationSummary: string | null = null;
    onAttributesReceived: ((attributes: any) => void) | null = null;

    constructor(
        private zone: NgZone,
        private callSummaryService: CallSummaryService,
        private callIntentService: CallIntentService,
        private callerService: CallerService,
        private router: Router,
        private http: HttpClient,
        private store: Store,
        @Inject(APP_CONFIG) private config: AppConfig
    ) {}

    private async fetchContactAttributes(contactId: string): Promise<any> {
        const headers = new HttpHeaders({
            'x-api-key': this.config.callSummaryApiKey,
            'Content-Type': 'application/json',
        });

        return firstValueFrom(
            this.http.post<any>(
                this.config.callAttributesApi,
                { action: 'getContactAttributes', contactId },
                { headers }
            )
        );
    }

    private isInsideAmazonConnect(): boolean {
        return window.self !== window.top;
    }

    private resolvePolicyNumbers(raw: any): { selected: string; others: string[] } | null {
        if (!raw) return null;

        let policies: string[] = [];

        if (Array.isArray(raw)) {
            policies = raw.map((p) => String(p).trim()).filter(Boolean);
        } else if (typeof raw === 'string') {
            try {
                const parsed = JSON.parse(raw);
                if (Array.isArray(parsed)) {
                    policies = parsed.map((p) => String(p).trim()).filter(Boolean);
                } else {
                    policies = [String(raw).trim()];
                }
            } catch {
                policies = raw.includes(',')
                    ? raw
                          .split(',')
                          .map((p: string) => p.trim())
                          .filter(Boolean)
                    : [raw.trim()];
            }
        }

        if (!policies.length) return null;

        const selected = policies[policies.length - 1];
        const others = policies.slice(0, policies.length - 1);
        return { selected, others };
    }

    /**
     * Extracts unique policy numbers from the authenticatedPolicies array,
     * preserving the original array order.
     * Returns { selected, others } where:
     *   - selected = the first unique policy number (last authenticated policy)
     *   - others   = all remaining unique policy numbers in order of first appearance
     */
    private resolveAuthenticatedPolicies(
        authenticatedPolicies: { policynumber: string; effectivedate: string; address?: string; lob?: string }[]
    ): { selected: string; selectedLob: string; others: { policyNumber: string; lob: string }[] } | null {
        if (!Array.isArray(authenticatedPolicies) || !authenticatedPolicies.length) return null;

        const seen = new Set<string>();
        const unique: { policyNumber: string; lob: string }[] = [];
        for (const entry of authenticatedPolicies) {
            const num = entry.policynumber?.trim();
            if (!num || seen.has(num)) continue;
            seen.add(num);
            unique.push({ policyNumber: num, lob: entry.lob ?? '' });
        }

        if (!unique.length) return null;

        const last = unique[unique.length - 1];
        return {
            selected: last.policyNumber,
            selectedLob: last.lob,
            others: unique.slice(0, unique.length - 1),
        };
    }

    async init(): Promise<void> {
        if (this.initialized) return;
        this.initialized = true;

        if (!this.isInsideAmazonConnect()) {
            // this.callSummary =
            //     'Customer contacted support because they received a notice about an Unclaimed Property Check and wanted to know how to claim it. Agent explained the process for claiming the Unclaimed Property Check.';

            // this.callIntentService.getCallIntent(this.callSummary).subscribe({
            //     next: (response) => {
            //         const body =
            //             typeof response?.body === 'string' ? JSON.parse(response.body) : response?.body ?? response;
            //         console.log('[Local Test] Call Intent Response:', body);
            //     },
            //     error: (err) => console.error('[Local Test] Call Intent failed:', err),
            // });

            // this.contactId = '0a9d4d85-cc90-4cfe-aadd-0ff47e28ff92'; // a real contactId from dev
            // this.channel = 'CHAT';

            // // Test call summary API
            // this.callSummaryService.getCallSummary(this.contactId, this.channel).subscribe({
            //     next: (res) => {
            //         this.callSummary = this.callSummaryService.extractSummaryText(res) ?? 'Fallback mock summary';
            //     },
            //     error: () => {
            //         this.callSummary = 'Fallback mock summary';
            //     },
            // });

            return;
        }

        return new Promise((resolve) => {
            AmazonConnectApp.init({
                onCreate: async (event: AppCreateEvent) => {
                    try {
                        const client = new ContactClient();
                        let contactId = await client.getInitialContactId(AppContactScope.CurrentContactId);
                        if (!contactId) {
                            if (event?.context?.scope && 'contactId' in event.context.scope) {
                                contactId = event.context.scope.contactId;
                            }
                        }

                        console.log('Resolved Target Contact ID:', contactId);

                        const contactDetails: any = await client.getContact(contactId!);
                        const getAttributes = await client.getAttributes(contactId!, [
                            'callBackNumber',
                            'callerNumber',
                            'escalationSummary',
                        ]);

                        let contactAttributesResponse: any = null;
                        try {
                            contactAttributesResponse = await this.fetchContactAttributes(contactId!);
                        } catch (err) {
                            console.error('Error fetching contact attributes from service:', err);
                        }

                        let channel: string | null = contactDetails?.Channel || null;
                        try {
                            const contactDetailsOnCreate: any = await client.getContact(contactId!);
                            channel = channel || contactDetailsOnCreate?.Channel || null;
                        } catch {
                            console.warn('Channel not available yet');
                        }

                        const normalizedResponseAttributes =
                            contactAttributesResponse && typeof contactAttributesResponse === 'object'
                                ? contactAttributesResponse
                                : {};
                        const customerPhoneNumber =
                            normalizedResponseAttributes?.CustomerNumber ||
                            normalizedResponseAttributes?.CustomerPhoneNumber ||
                            getAttributes?.CustomerPhoneNumber ||
                            getAttributes?.CustomerNumber ||
                            null;
                        const callBackNumber =
                            normalizedResponseAttributes?.callBackNumber || getAttributes?.callBackNumber || null;
                        const responseChannel =
                            normalizedResponseAttributes?.contactChannel ||
                            normalizedResponseAttributes?.contactChannel?.toString() ||
                            null;
                        const normalizedChannel = (responseChannel || channel || null)?.toString();
                        const escalationSummary =
                            normalizedResponseAttributes?.escalationSummary ||
                            normalizedResponseAttributes?.EscalationSummary ||
                            getAttributes?.escalationSummary ||
                            getAttributes?.EscalationSummary ||
                            null;

                        this.zone.run(() => {
                            this.contactId = contactId || null;
                            this.channel = normalizedChannel || null;
                            this.callSummary = escalationSummary;
                            this.preEscalationSummary = escalationSummary;

                            this.attributes = {
                                ...getAttributes,
                                ...normalizedResponseAttributes,
                                CustomerPhoneNumber: customerPhoneNumber,
                                callBackNumber:
                                    normalizedResponseAttributes?.callBackNumber ||
                                    getAttributes?.callBackNumber ||
                                    null,
                                contactChannel:
                                    normalizedResponseAttributes?.contactChannel || normalizedChannel || undefined,
                            };

                            const persona = normalizedResponseAttributes?.Persona || normalizedResponseAttributes?.userType || normalizedResponseAttributes?.UserType || getAttributes?.Persona || getAttributes?.userType || '';
                            this.callerService.ivrSelectedRole.set(this.callerService.mapPersonaToRole(persona));

                            const userAuthenticated =
                                normalizedResponseAttributes?.userAuthenticated ||
                                normalizedResponseAttributes?.UserAuthenticated ||
                                getAttributes?.userAuthenticated ||
                                getAttributes?.UserAuthenticated ||
                                normalizedResponseAttributes?.isAuthenticated ||
                                normalizedResponseAttributes?.isuserAuthenticated ||
                                normalizedResponseAttributes?.isUserAuthenticated ||
                                getAttributes?.isAuthenticated ||
                                getAttributes?.isuserAuthenticated ||
                                getAttributes?.isUserAuthenticated ||
                                null;

                            const isAuthenticated =
                                userAuthenticated === true || userAuthenticated === 'true' || userAuthenticated === '1';

                            // Prefer authenticatedPolicies array when present; fall back to scalar policyNumber
                            const rawAuthenticatedPolicies = normalizedResponseAttributes?.authenticatedPolicies;
                            const resolved =
                                Array.isArray(rawAuthenticatedPolicies) && rawAuthenticatedPolicies.length
                                    ? this.resolveAuthenticatedPolicies(rawAuthenticatedPolicies)
                                    : this.resolvePolicyNumbers(
                                          normalizedResponseAttributes?.policyNumber ||
                                              normalizedResponseAttributes?.PolicyNumber ||
                                              getAttributes?.policyNumber ||
                                              getAttributes?.PolicyNumber ||
                                              null
                                      );

                            if (resolved && isAuthenticated) {
                                this.store.dispatch(PolicyActions.loadPolicyData({ policyNumber: resolved.selected }));
                                this.router.navigate(['/profile-activities'], {
                                    queryParams: { policyNumber: resolved.selected },
                                });
                                (resolved.others as any[]).forEach((item) =>
                                    this.store.dispatch(
                                        addRecentPolicy({
                                            policyNumber: typeof item === 'string' ? item : item.policyNumber,
                                            lineOfBusiness: typeof item === 'string' ? '' : item.lob,
                                        })
                                    )
                                );
                                this.store.dispatch(
                                    addRecentPolicy({
                                        policyNumber: resolved.selected,
                                        lineOfBusiness: (resolved as any).selectedLob ?? '',
                                    })
                                );
                            } else if (resolved) {
                                (resolved.others as any[]).forEach((item) =>
                                    this.store.dispatch(
                                        addRecentPolicy({
                                            policyNumber: typeof item === 'string' ? item : item.policyNumber,
                                            lineOfBusiness: typeof item === 'string' ? '' : item.lob,
                                        })
                                    )
                                );
                                this.store.dispatch(
                                    addRecentPolicy({
                                        policyNumber: resolved.selected,
                                        lineOfBusiness: (resolved as any).selectedLob ?? '',
                                    })
                                );
                            }

                            const phoneForLookup = callBackNumber || customerPhoneNumber;
                            if (phoneForLookup) {
                                const normalizedPhone = phoneForLookup.startsWith('+1')
                                    ? phoneForLookup.slice(2)
                                    : phoneForLookup;
                                this.callerService
                                    .lookupCallerByPhone(normalizedPhone, this.config.phoneSearchApi)
                                    .subscribe({
                                        next: () => console.log('BMC contacts loaded'),
                                        error: (err) => console.error('Phone lookup failed:', err),
                                    });
                            }
                        });
                    } catch (err) {
                        console.error('Error getting initial contact id:', err);
                    } finally {
                        resolve();
                    }
                },
                onDestroy: async () => {
                    if (!this.contactId || !this.channel) {
                        return;
                    }
                    try {
                        this.callSummaryService.getCallSummary(this.contactId, this.channel).subscribe({
                            next: (response) => {
                                this.zone.run(() => {
                                    this.callSummary = this.callSummaryService.extractSummaryText(response);
                                });
                            },
                            error: (err) => console.error('Error fetching call summary:', err),
                        });
                    } catch (err) {
                        console.error('Error in onDestroy:', err);
                    }
                },
            });
        });
    }
}
